import { useEffect, useRef } from 'react';
import { useTour } from '@reactour/tour';
import { getActiveTourKey } from './activeTour';
import { CANMEET_INSTANT_STEP, CANMEET_SCHEDULED_STEP } from './tourRegistry';

// Action-driven gates + adaptive steps for the two CANmeet tours. Called from
// CreateMeetingModal, which holds the dialog-open flag (`open`), the selected
// mode, and the recurring toggle. Gates:
//   CREATE -> MODE   when the Create Meeting dialog opens (both tours)
//   MODE   -> NAME   when the user switches to "schedule" (scheduled tour only)
// Adaptive recurrence (scheduled tour): the RECURRENCE step's target (the
// frequency dropdown) only exists while "Recurring" is on, so we never sit on a
// reflowing step or a missing element:
//   - Rising edge of "Recurring" while on the RECURRING step advances to
//     RECURRENCE, following the user's action onto the freshly-mounted dropdown.
//   - On the RECURRENCE step while "Recurring" is off, we skip it in whichever
//     direction the user is moving (forward -> INVITE, backward -> RECURRING) so
//     Back still works.
export const useCanmeetTourSync = (
	dialogOpen: boolean,
	meetingMode: 'instant' | 'schedule',
	isRecurring: boolean,
) => {
	const { isOpen, currentStep, setCurrentStep } = useTour();
	const prevStepRef = useRef(currentStep);
	const prevRecurringRef = useRef(isRecurring);
	const prevDialogOpenRef = useRef(dialogOpen);

	useEffect(() => {
		const recurringTurnedOn = isRecurring && !prevRecurringRef.current;
		prevRecurringRef.current = isRecurring;
		const dialogJustClosed = prevDialogOpenRef.current && !dialogOpen;
		prevDialogOpenRef.current = dialogOpen;

		if (!isOpen) {
			prevStepRef.current = currentStep;
			return;
		}
		const key = getActiveTourKey();

		if (key === 'canmeet_instant_meeting') {
			if (currentStep === CANMEET_INSTANT_STEP.CREATE && dialogOpen) {
				setCurrentStep(CANMEET_INSTANT_STEP.MODE);
			} else if (
				currentStep === CANMEET_INSTANT_STEP.MODE &&
				meetingMode === 'instant'
			) {
				setCurrentStep(CANMEET_INSTANT_STEP.NAME);
			} else if (
				currentStep === CANMEET_INSTANT_STEP.LAUNCH &&
				dialogJustClosed
			) {
				setCurrentStep(CANMEET_INSTANT_STEP.FINISH);
			}
		} else if (key === 'canmeet_scheduled_meeting') {
			if (
				currentStep === CANMEET_SCHEDULED_STEP.SCHEDULE &&
				dialogJustClosed
			) {
				// Sandboxed "schedule" closed the dialog — finish the tour.
				setCurrentStep(CANMEET_SCHEDULED_STEP.FINISH);
			} else if (currentStep === CANMEET_SCHEDULED_STEP.CREATE && dialogOpen) {
				setCurrentStep(CANMEET_SCHEDULED_STEP.MODE);
			} else if (
				currentStep === CANMEET_SCHEDULED_STEP.MODE &&
				meetingMode === 'schedule'
			) {
				setCurrentStep(CANMEET_SCHEDULED_STEP.NAME);
			} else if (
				currentStep === CANMEET_SCHEDULED_STEP.RECURRING &&
				recurringTurnedOn
			) {
				setCurrentStep(CANMEET_SCHEDULED_STEP.RECURRENCE);
			} else if (
				currentStep === CANMEET_SCHEDULED_STEP.RECURRENCE &&
				!isRecurring
			) {
				const movingBack = prevStepRef.current > currentStep;
				setCurrentStep(
					movingBack
						? CANMEET_SCHEDULED_STEP.RECURRING
						: CANMEET_SCHEDULED_STEP.INVITE,
				);
			}
		}

		prevStepRef.current = currentStep;
	}, [isOpen, currentStep, dialogOpen, meetingMode, isRecurring, setCurrentStep]);
};
